import React, {
  ChangeEvent,
  DragEvent,
  useEffect,
  useRef,
  useState,
  useCallback
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  SFormPhoto,
  SFormPhotoBody,
  SFormPhotoCaption,
  SFormPhotoHeader,
  SFormPhotoDropzone
} from './styled';
import { Icon } from '@/presentation/reusables/components/Icon';
import { ValidationResult } from './components/ValidationResult';
import { ErrorMessage } from './components/ErrorMessage';
import { Button } from '@/presentation/reusables/components/Button';
import Processing from '@/presentation/components/Processing';
import Dialog from '@/presentation/reusables/Dialog';
import DialogExtraCaption from '@/presentation/reusables/Dialog/extras/caption';
import svgAlert from './assets/alert-triangle.svg';
import { Creators as uploadValidationCreators } from '../../../../../domain/useCases/uploadValidation';

type FormPhotoProps = {
  isValid?: string;
  onRegister: (src?: string) => void;
  photo?: string;
  hasPhoto?: boolean;
  setFileExists: (v: boolean) => void;
};

interface ValidationItem {
  id: string;
  isValid: boolean;
}

export const FormPhoto = ({
  isValid,
  onRegister,
  photo,
  setFileExists,
  hasPhoto
}: FormPhotoProps) => {
  const [currentFile, setCurrentFile] = useState<File>();
  const [error, setError] = useState<string>();
  const [isDisabled, setIsDisabled] = useState(true);
  const [isDrag, setIsDrag] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isShow, setIsShow] = useState(false);
  const [result, setResult] = useState<ValidationItem[]>();
  const [size, setSize] = useState(0);
  const [src, setSrc] = useState<string>();
  const [fileUploaded, setFileUploaded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const dispatch = useDispatch();
  const uploadValidationData = useSelector(
    (state: any) => state.uploadValidation.data
  );
  const uploadValidationLoading = useSelector(
    (state: any) => state.uploadValidation.loading
  );
  const uploadValidationError = useSelector(
    (state: any) => state.uploadValidation.error
  );

  const resetState = useCallback(() => {
    setCurrentFile(undefined);
    setError(undefined);
    setFileExists(false);
    setIsDisabled(true);
    setIsLoading(false);
    setIsShow(false);
    setResult(undefined);
    setSize(0);
    setSrc(undefined);
  }, [setFileExists]);

  const handleFileValidation = useCallback(
    (file?: File | null) => {
      resetState();
      if (!file) return;

      const fileName = file.name;
      const fileSize = Number((file.size / 1024).toFixed(0));
      const fileExtension = fileName.split('.').pop()?.toLowerCase();

      if (fileExtension && ['jpg', 'jpeg'].includes(fileExtension)) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setSrc(reader.result as string);
        };
        reader.readAsDataURL(file);
        setSize(fileSize);

        if (fileSize > 50) {
          setError(
            '¡Error de carga! Tu archivo superó el tamaño máximo requerido.'
          );
          setCurrentFile(undefined);
        } else {
          setFileExists(true);
          setCurrentFile(file);
          setIsDisabled(false);
        }
      } else {
        setError('¡Error de carga! Solo se permiten archivos .jpg y .jpeg.');
      }
    },
    [resetState, setFileExists]
  );

  const handleValidate = useCallback(() => {
    if (currentFile && src) {
      setIsLoading(true);
      const base64 = src.split(',')[1] || '';
      dispatch(uploadValidationCreators.uploadValidation(base64));
    }
  }, [currentFile, src, dispatch]);

  useEffect(() => {
    if (uploadValidationData && Array.isArray(uploadValidationData)) {
      setResult(uploadValidationData);
      setFileUploaded(true);
      setIsLoading(false);
    }
  }, [uploadValidationData]);

  useEffect(() => {
    if (uploadValidationError) {
      setError(uploadValidationError);
      setIsLoading(false);
    }
  }, [uploadValidationError]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    handleFileValidation(file);
    e.target.value = '';
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDrag(true);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFileValidation(file);
    setIsDrag(false);
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDrag(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDrag(false);
  };

  const openFileDialog = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const createVirtualFile = useCallback((data?: string) => {
    if (data) {
      const base64 = data.split(',')[1];
      const byteCharacters = atob(base64);
      const byteNumbers = Array.from(
        { length: byteCharacters.length },
        (_, i) => byteCharacters.charCodeAt(i)
      );
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/jpeg' });
      const file = new File([blob], 'virtual.jpeg', { type: 'image/jpeg' });
      setCurrentFile(file);
    }
  }, []);

  useEffect(() => {
    if (isValid !== undefined) {
      if (hasPhoto && photo) {
        setSrc(photo);
        createVirtualFile(photo);
      }
      setIsDisabled(isValid === 'true');
    }
  }, [isValid, hasPhoto, photo, createVirtualFile]);

  return (
    <>
      <SFormPhoto>
        <SFormPhotoHeader>
          <p>Validación de foto</p>
          <p>
            {src
              ? 'Verifica si tu foto cumple con los requisitos de SUNEDU.'
              : 'Verifica si tu foto cumple con los requisitos necesarios para registrarla.'}
          </p>
        </SFormPhotoHeader>
        <SFormPhotoCaption isInvalidSize={size > 50}>
          <div>
            <p>
              Tamaño total:{' '}
              <b>
                <span>{size} kb</span> / 50 kb
              </b>
            </p>
          </div>
          {error && <ErrorMessage message={error} />}
        </SFormPhotoCaption>
        <SFormPhotoBody>
          <SFormPhotoDropzone
            data-testid="cmp-drop-zone-photo"
            isDrag={isDrag}
            onDrop={handleDrop}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
          >
            <input
              data-testid="input-photo"
              ref={inputRef}
              accept=".jpg, .jpeg"
              type="file"
              onChange={handleChange}
            />
            {src ? (
              <img src={src} alt="Vista previa de la imagen" />
            ) : (
              <p>
                {isDrag ? 'Suelta aquí.' : 'Arrastra o selecciona tu archivo.'}
              </p>
            )}
          </SFormPhotoDropzone>
          <Button
            variant="secondary"
            fullWidth="640px"
            size="small"
            onClick={openFileDialog}
          >
            <Icon name={src ? 'reload' : 'upload'} />
            {src ? 'Cambiar foto' : 'Cargar foto'}
          </Button>
          {result && (
            <ValidationResult
              result={result}
              setIsValid={(v) => setIsDisabled(!v)}
            />
          )}
          {src && isDisabled && !fileUploaded && !error ? (
            <div
              style={{
                height: 36,
                backgroundColor: '#DEFBE6',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '0 8px'
              }}
            >
              <img
                style={{ height: 16, width: 16 }}
                src={svgAlert}
                alt="Alerta"
              />
              <p
                style={{
                  color: '#005C27',
                  fontSize: '12px',
                  lineHeight: '16px'
                }}
              >
                Tu foto ya está validada.
              </p>
            </div>
          ) : (
            <Button
              variant="primary"
              isDisabled={isDisabled}
              size="medium"
              fullWidth="640px"
              onClick={() => (result ? setIsShow(true) : handleValidate())}
            >
              {result ? 'Enviar' : 'Validar'}
            </Button>
          )}
        </SFormPhotoBody>
      </SFormPhoto>
      <Processing isOpen={isLoading || uploadValidationLoading} />
      <Dialog
        handlerClose={() => setIsShow(false)}
        maxWidth="360px"
        show={isShow}
        zIndex={1000}
        textButtonA="Sí, enviarlo"
        handlerButtonA={() => {
          setIsShow(false);
          onRegister(src);
        }}
        textButtonB="Cancelar"
        handlerButtonB={() => setIsShow(false)}
      >
        <DialogExtraCaption
          subtitle="Recuerda que se registrará de forma inmediata."
          title="¿Deseas enviar esta foto?"
        />
      </Dialog>
    </>
  );
};
